import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FileUploadSection, QuickSettings, GeneratedQuestionsPreview } from './';
import MarkdownEditor from './MarkdownEditor';
import {
  textToMarkdown,
  splitMarkdownIntoSections,
  mergeSectionsToMarkdown,
  getTextStats,
  cleanMarkdownForLLM
} from '../../utils/llm-generator/markdownConverter';

/**
 * EditorWorkflow Component
 * Full control workflow with Markdown editing before sending to LLM
 */
export default function EditorWorkflow({
  // File Upload Props
  llmProvider,
  useNativeFileUpload,
  setUseNativeFileUpload,
  uploadedFiles,
  fileContents,
  onFileUpload,
  onRemoveFile,
  formatFileSize,
  
  // Generation Settings
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  
  // Generation
  apiKey,
  isGenerating,
  generationLog,
  onGenerate,
  
  // Results
  generatedQuestions,
  setGeneratedQuestions,
  validationErrors,
  editingIndex,
  editedQuestion,
  setEditedQuestion,
  selectedSession,
  getQualityScore,
  findDuplicates,
  validateAllQuestions,
  onImportQuestions,
  onRerollQuestion,
  onGenerateVariation,
  onToggleMultipleChoice,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onSaveToSession,
  onDownloadQuestions,
  onRefineQuestion
}) {
  // Editor workflow state
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isConverted, setIsConverted] = useState(false);
  const [selectedSectionsForGeneration, setSelectedSectionsForGeneration] = useState([]);

  // Convert uploaded files to markdown sections when files change
  useEffect(() => {
    if ((fileContents?.length || 0) > 0 && !isConverted) {
      convertToMarkdown();
    }
  }, [fileContents]);

  // Convert file contents to markdown
  const convertToMarkdown = useCallback(() => {
    const combinedContent = fileContents
      .map(f => `# ${f.name}\n\n${f.content}`)
      .join('\n\n---\n\n');
    
    const markdown = textToMarkdown(combinedContent, {
      preserveFormatting: true,
      detectHeaders: true
    });
    
    const newSections = splitMarkdownIntoSections(markdown, {
      minLevel: 1,
      maxLevel: 3
    });
    
    setSections(newSections);
    setSelectedSectionId(newSections[0]?.id || null);
    setSelectedSectionsForGeneration(newSections.map(s => s.id));
    setIsConverted(true);
  }, [fileContents]);

  // Reset when files are removed
  useEffect(() => {
    if ((fileContents?.length || 0) === 0) {
      setSections([]);
      setSelectedSectionId(null);
      setIsConverted(false);
      setSelectedSectionsForGeneration([]);
    }
  }, [fileContents?.length]);

  // Toggle section selection for generation
  const toggleSectionSelection = useCallback((sectionId) => {
    setSelectedSectionsForGeneration(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  }, []);

  // Select/deselect all sections
  const selectAllSections = useCallback(() => {
    setSelectedSectionsForGeneration(sections.map(s => s.id));
  }, [sections]);

  const deselectAllSections = useCallback(() => {
    setSelectedSectionsForGeneration([]);
  }, []);

  // Get content from selected sections
  const selectedContent = useMemo(() => {
    const selectedSections = sections.filter(s => 
      selectedSectionsForGeneration.includes(s.id)
    );
    const markdown = mergeSectionsToMarkdown(selectedSections);
    return cleanMarkdownForLLM(markdown);
  }, [sections, selectedSectionsForGeneration]);

  // Statistics for selected content
  const selectedStats = useMemo(() => {
    return getTextStats(selectedContent);
  }, [selectedContent]);

  // Handle generate
  const handleGenerate = useCallback(() => {
    onGenerate(selectedContent);
  }, [onGenerate, selectedContent]);

  const hasContent = selectedSectionsForGeneration.length > 0 && selectedContent.length > 0;
  const canGenerate = hasContent && (apiKey || llmProvider === 'custom');

  return (
    <div className="space-y-4">
      {/* Two-column layout for file upload and quick settings when no content */}
      {!isConverted && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FileUploadSection
              llmProvider={llmProvider}
              useNativeFileUpload={useNativeFileUpload}
              setUseNativeFileUpload={setUseNativeFileUpload}
              uploadedFiles={uploadedFiles}
              fileContents={fileContents}
              onFileUpload={onFileUpload}
              onRemoveFile={onRemoveFile}
              formatFileSize={formatFileSize}
            />
            
            {fileContents.length > 0 && (
              <button
                onClick={convertToMarkdown}
                className="w-full py-3 px-4 bg-secondary-600 text-white rounded-lg font-medium hover:bg-secondary-700 theme-transition"
              >
                📝 In Markdown konvertieren
              </button>
            )}
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center flex flex-col justify-center min-h-[300px]">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
              Editor Workflow
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
              Volle Kontrolle über den Content vor der Generierung
            </p>
            
            <div className="text-left bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-xs text-gray-600 dark:text-gray-400">
              <p className="font-medium mb-2">So funktioniert's:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Dokument hochladen</li>
                <li>In Markdown konvertieren</li>
                <li>Abschnitte bearbeiten/löschen</li>
                <li>Relevante Abschnitte auswählen</li>
                <li>Fragen generieren</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Editor view when content is converted */}
      {isConverted && sections.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Left: Editor (takes 2 columns on xl) */}
          <div className="xl:col-span-2 space-y-4">
            {/* Section selection bar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Abschnitte für Generierung
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllSections}
                    className="text-xs px-2 py-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
                  >
                    Alle
                  </button>
                  <button
                    onClick={deselectAllSections}
                    className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    Keine
                  </button>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => toggleSectionSelection(section.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium theme-transition ${
                      selectedSectionsForGeneration.includes(section.id)
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {index + 1}. {section.title.slice(0, 20)}{section.title.length > 20 ? '...' : ''}
                  </button>
                ))}
              </div>
              
              {/* Selection stats */}
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                <span>{selectedSectionsForGeneration.length}/{sections.length} ausgewählt</span>
                <span>{selectedStats.words} Wörter</span>
                <span>~{selectedStats.estimatedTokens.toLocaleString()} Tokens</span>
              </div>
            </div>

            {/* Markdown Editor */}
            <MarkdownEditor
              sections={sections}
              onSectionsChange={setSections}
              selectedSectionId={selectedSectionId}
              onSelectSection={setSelectedSectionId}
            />

            {/* Reset button */}
            <button
              onClick={() => {
                setIsConverted(false);
                setSections([]);
              }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Zurück zur Dateiauswahl
            </button>
          </div>

          {/* Right column: Settings, Generate, Results */}
          <div className="space-y-4">
            <QuickSettings
              numberOfQuestions={numberOfQuestions}
              setNumberOfQuestions={setNumberOfQuestions}
              difficulty={difficulty}
              setDifficulty={setDifficulty}
              includeCodeBlocks={includeCodeBlocks}
              setIncludeCodeBlocks={setIncludeCodeBlocks}
              userGuidance={userGuidance}
              setUserGuidance={setUserGuidance}
              topicTags={topicTags}
              currentTag={currentTag}
              setCurrentTag={setCurrentTag}
              onAddTopicTag={onAddTopicTag}
              onRemoveTopicTag={onRemoveTopicTag}
            />

            {/* Generate Button */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !canGenerate}
                className={`
                  w-full py-3 px-6 rounded-lg font-semibold transition-all
                  ${isGenerating || !canGenerate
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                  }
                `}
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generiere...
                  </span>
                ) : (
                  <span>{numberOfQuestions} Fragen aus Editor</span>
                )}
              </button>
              
              {!canGenerate && (
                <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">
                  {selectedSectionsForGeneration.length === 0
                    ? 'Abschnitte auswählen'
                    : 'API Key fehlt - siehe Einstellungen'
                  }
                </p>
              )}
            </div>

            {/* Content Preview */}
            {selectedContent && (
              <details className="bg-gray-900 rounded-lg overflow-hidden">
                <summary className="px-4 py-2 text-xs text-gray-400 cursor-pointer hover:bg-gray-800">
                  Content-Vorschau ({selectedStats.chars.toLocaleString()} chars)
                </summary>
                <pre className="px-4 pb-4 text-gray-300 text-xs overflow-auto max-h-48 font-mono whitespace-pre-wrap">
                  {selectedContent.slice(0, 2000)}
                  {selectedContent.length > 2000 && '\n\n... (gekürzt)'}
                </pre>
              </details>
            )}

            {/* Generation Log */}
            {generationLog && (
              <details className="bg-gray-900 rounded-lg overflow-hidden">
                <summary className="px-4 py-2 text-xs text-gray-400 cursor-pointer hover:bg-gray-800">
                  Log anzeigen
                </summary>
                <pre className="px-4 pb-4 text-success-400 text-xs overflow-auto max-h-32 font-mono whitespace-pre-wrap">
                  {generationLog}
                </pre>
              </details>
            )}

            {/* Results */}
            {generatedQuestions.length > 0 && (
              <GeneratedQuestionsPreview
                questions={generatedQuestions}
                setQuestions={setGeneratedQuestions}
                validationErrors={validationErrors}
                editingIndex={editingIndex}
                editedQuestion={editedQuestion}
                setEditedQuestion={setEditedQuestion}
                isGenerating={isGenerating}
                selectedSession={selectedSession}
                getQualityScore={getQualityScore}
                findDuplicates={findDuplicates}
                validateAllQuestions={validateAllQuestions}
                onImportQuestions={onImportQuestions}
                onRerollQuestion={onRerollQuestion}
                onGenerateVariation={onGenerateVariation}
                onToggleMultipleChoice={onToggleMultipleChoice}
                onStartEditing={onStartEditing}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onSaveToSession={onSaveToSession}
                onDownloadQuestions={onDownloadQuestions}
                onRefineQuestion={onRefineQuestion}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

EditorWorkflow.propTypes = {
  // File Upload
  llmProvider: PropTypes.string.isRequired,
  useNativeFileUpload: PropTypes.bool.isRequired,
  setUseNativeFileUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  fileContents: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  formatFileSize: PropTypes.func.isRequired,
  
  // Generation Settings
  numberOfQuestions: PropTypes.number.isRequired,
  setNumberOfQuestions: PropTypes.func.isRequired,
  difficulty: PropTypes.string.isRequired,
  setDifficulty: PropTypes.func.isRequired,
  includeCodeBlocks: PropTypes.bool.isRequired,
  setIncludeCodeBlocks: PropTypes.func.isRequired,
  userGuidance: PropTypes.string,
  setUserGuidance: PropTypes.func.isRequired,
  topicTags: PropTypes.array.isRequired,
  currentTag: PropTypes.string.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  onAddTopicTag: PropTypes.func.isRequired,
  onRemoveTopicTag: PropTypes.func.isRequired,
  
  // Generation
  apiKey: PropTypes.string,
  isGenerating: PropTypes.bool.isRequired,
  generationLog: PropTypes.string,
  onGenerate: PropTypes.func.isRequired,
  
  // Results
  generatedQuestions: PropTypes.array.isRequired,
  setGeneratedQuestions: PropTypes.func.isRequired,
  validationErrors: PropTypes.array.isRequired,
  editingIndex: PropTypes.number,
  editedQuestion: PropTypes.object,
  setEditedQuestion: PropTypes.func.isRequired,
  selectedSession: PropTypes.object,
  getQualityScore: PropTypes.func.isRequired,
  findDuplicates: PropTypes.func.isRequired,
  validateAllQuestions: PropTypes.func.isRequired,
  onImportQuestions: PropTypes.func.isRequired,
  onRerollQuestion: PropTypes.func.isRequired,
  onGenerateVariation: PropTypes.func.isRequired,
  onToggleMultipleChoice: PropTypes.func.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveToSession: PropTypes.func.isRequired,
  onDownloadQuestions: PropTypes.func.isRequired,
  onRefineQuestion: PropTypes.func
};
